import { useState } from "react";
import { useAppContext } from "../App";

interface Prompt {
  id: number;
  text: string;
  created_at: string;
  closes_at: string | null;
}

interface AdminPanelProps {
  prompts: Prompt[];
  onClose: () => void;
}

export function AdminPanel({ prompts, onClose }: AdminPanelProps) {
  const { refreshData } = useAppContext();
  const [newPromptText, setNewPromptText] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreatePrompt = async () => {
    if (!newPromptText.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/prompt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: newPromptText }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to create prompt");
      }
      setNewPromptText("");
      refreshData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create prompt");
    } finally {
      setCreating(false);
    }
  };

  const handleClosePrompt = async (promptId: number) => {
    try {
      const res = await fetch(`/api/admin/prompt/${promptId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ closesAt: new Date().toISOString() }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to close prompt");
      }
      refreshData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to close prompt");
    }
  };

  const handleReopenPrompt = async (promptId: number) => {
    try {
      const res = await fetch(`/api/admin/prompt/${promptId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ closesAt: null }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to reopen prompt");
      }
      refreshData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reopen prompt");
    }
  };

  const handleDeletePrompt = async (promptId: number) => {
    if (!confirm("Are you sure? This will delete the prompt and ALL its submissions.")) return;
    try {
      const res = await fetch(`/api/admin/prompt/${promptId}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to delete prompt");
      }
      refreshData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete prompt");
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal admin-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Admin Panel</h2>

        {error && <p className="error-message">{error}</p>}

        <div className="admin-section">
          <h3>Create New Prompt</h3>
          <div className="form-group">
            <textarea
              value={newPromptText}
              onChange={(e) => setNewPromptText(e.target.value)}
              placeholder="Enter the prompt text..."
              rows={3}
            />
          </div>
          <button
            className="btn-primary"
            onClick={handleCreatePrompt}
            disabled={creating || !newPromptText.trim()}
          >
            {creating ? "Creating..." : "Create Prompt"}
          </button>
        </div>

        <div className="admin-section">
          <h3>Manage Prompts</h3>
          <div className="prompt-list">
            {prompts.map((prompt) => (
              <div key={prompt.id} className="prompt-item">
                <div className="prompt-item-text">
                  "{prompt.text}"
                  {prompt.closes_at && (
                    <span className="prompt-closed-badge">Closed</span>
                  )}
                </div>
                <div className="prompt-item-actions">
                  {prompt.closes_at ? (
                    <button
                      className="btn-small"
                      onClick={() => handleReopenPrompt(prompt.id)}
                    >
                      Reopen
                    </button>
                  ) : (
                    <button
                      className="btn-small"
                      onClick={() => handleClosePrompt(prompt.id)}
                    >
                      Close
                    </button>
                  )}
                  <button
                    className="btn-small btn-danger"
                    onClick={() => handleDeletePrompt(prompt.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
